/**
 * Admin configuration client — the `sync-scope` configuration behind
 * `/ivt/admin/configuration`.
 *
 * One operation pair serves every configuration; the `ConfigurationValue` union member
 * says which one. This module is deliberately narrow: it reads and writes the
 * `syncScope` member only, and returns the `revision` verbatim so the page can echo it
 * back on save.
 *
 * `revision` is an opaque string. Do not parse or increment it — the service issues it,
 * the page returns it, and a mismatch is the service telling you someone else committed
 * first.
 */

import {
  getIvtClient,
  GetConfigurationCommand,
  UpdateConfigurationCommand,
  type SyncScopeConfiguration,
} from './IvtHubClient';

/** The only configuration key today. Mirrors the service's `ConfigurationKey` enum. */
export const SYNC_SCOPE_KEY = 'sync-scope';

/**
 * A loaded configuration plus what the page needs to save it back and to show who
 * touched it last.
 */
export interface SyncScopeSnapshot {
  config: SyncScopeConfiguration;
  revision: string;
  updatedAt?: Date;
  updatedBy?: string;
}

/**
 * Load the sync-scope configuration.
 *
 * Throws on failure rather than returning a sentinel: unlike the industry-permission
 * fetch, there is no fail-closed reading to fall back to. An admin who cannot load the
 * page needs the error, and rendering a default would show a scope the sync is not
 * using — which looks correct and is not.
 */
export async function fetchSyncScope(): Promise<SyncScopeSnapshot> {
  const out = await getIvtClient().send(
    new GetConfigurationCommand({ configurationKey: SYNC_SCOPE_KEY }),
  );
  const config = out.configuration?.syncScope;
  if (!config || !out.revision) {
    // The service returns both or errors; this guards the generated type's optionality
    // rather than a real branch.
    throw new Error('GetConfiguration returned no syncScope payload');
  }
  return {
    config,
    revision: out.revision,
    updatedAt: out.updatedAt,
    updatedBy: out.updatedBy,
  };
}

/**
 * Replace the sync-scope configuration.
 *
 * `revision` must be the value from the snapshot the user edited. A stale one yields
 * `ConflictException` (409), which the page surfaces as "someone else saved — reload",
 * NOT as a validation error: nothing the user typed is wrong.
 *
 * Returns the post-save snapshot. The service returns the full resource, so the page
 * needs no follow-up read — which matters because the response carries fields the page
 * never sent (each industry's `sfdcShLevel` and `updatedBy`, plus `updatedAt`).
 */
export async function saveSyncScope(
  config: SyncScopeConfiguration,
  revision: string,
): Promise<SyncScopeSnapshot> {
  const out = await getIvtClient().send(
    new UpdateConfigurationCommand({
      configurationKey: SYNC_SCOPE_KEY,
      configuration: { syncScope: config },
      revision,
    }),
  );
  const saved = out.configuration?.syncScope;
  if (!saved || !out.revision) {
    throw new Error('UpdateConfiguration returned no syncScope payload');
  }
  return {
    config: saved,
    revision: out.revision,
    updatedAt: out.updatedAt,
    updatedBy: out.updatedBy,
  };
}

/** True when the caught error is the optimistic-lock conflict (someone else saved). */
export function isRevisionConflict(e: unknown): boolean {
  return (e as { name?: string } | null)?.name === 'ConflictException';
}
